import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Send } from 'lucide-react';

import { approveRock, rejectRock, submitRock, type Rock, type RockApprover } from '@/api/eos/rocks.api';
import { Button } from '@/components/ui/Button';
import { describeEosError, eosErrorCode } from '@/lib/eos/errors';
import { useToast } from '@/hooks/useToast';

/**
 * Approval-lifecycle section of the rock detail drawer (Phase H — Rocks
 * workflow). Drives the draft → pending_approval → active flow:
 *
 *   - draft            → owners/editors get a "Submit for approval" button
 *                        (and the last rejection reason, if any).
 *   - pending_approval → shows the outstanding approver slots; eligible
 *                        viewers get Approve / Reject (with an optional reason).
 *   - active           → a subtle "Active" confirmation.
 *
 * The server is authoritative on eligibility — it returns `not_an_approver` /
 * `not_pending` / `not_draft`, which are mapped to friendly toasts here. Every
 * mutation invalidates `['eos','rocks']` so the drawer re-derives off fresh
 * list data.
 */

const APPROVER_LABEL: Record<RockApprover, string> = {
  integrator: 'Integrator',
  manager: 'Manager',
};

const TEXTAREA_CLS =
  'w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

function describeApprovalError(err: unknown, fallback: string): string {
  switch (eosErrorCode(err)) {
    case 'not_draft':
      return 'This rock is no longer a draft — refresh the page.';
    case 'not_pending':
      return 'This rock is not awaiting approval anymore — refresh the page.';
    case 'not_an_approver':
      return 'You are not one of this rock’s approvers.';
    default:
      return describeEosError(err, fallback);
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString();
}

export function RockApprovalSection({
  rock,
  canEdit,
}: {
  rock: Rock;
  /** The rock's owner or an editor — may submit a draft. */
  canEdit: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'rocks'] });
  }

  const submitMutation = useMutation({
    mutationFn: () => submitRock(rock.id),
    onSuccess: () => {
      invalidate();
      toast.show('Submitted for approval', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeApprovalError(err, 'Could not submit the rock.'), { variant: 'danger' });
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => approveRock(rock.id),
    onSuccess: () => {
      invalidate();
      toast.show('Approval recorded', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeApprovalError(err, 'Could not approve the rock.'), { variant: 'danger' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectRock(rock.id, { reason: reason.trim() || undefined }),
    onSuccess: () => {
      setRejecting(false);
      setReason('');
      invalidate();
      toast.show('Rock sent back', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeApprovalError(err, 'Could not reject the rock.'), { variant: 'danger' });
    },
  });

  // The server computes who may actually approve (an active integrator, or
  // the owner's reporting manager) — role alone is not a reliable proxy.
  const canApprove = rock.viewerCanApprove;
  const busy = submitMutation.isPending || approveMutation.isPending || rejectMutation.isPending;

  return (
    <div className="space-y-3">
      <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Approval</h3>

      {rock.approvalState === 'draft' ? (
        <div className="space-y-3">
          {rock.rejectedAt && rock.rejectionReason ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-kw-danger-text">Sent back</p>
                <p className="mt-0.5 text-[12px] text-kw-danger-text/90">{rock.rejectionReason}</p>
              </div>
            </div>
          ) : null}
          <p className="text-[12px] text-kw-muted">
            This rock is a draft — submit it to start the approval process.
          </p>
          {canEdit ? (
            <Button
              size="sm"
              isLoading={submitMutation.isPending}
              disabled={busy}
              onClick={() => {
                submitMutation.mutate();
              }}
            >
              <Send className="size-4" />
              Submit for approval
            </Button>
          ) : null}
        </div>
      ) : null}

      {rock.approvalState === 'pending_approval' ? (
        <div className="space-y-3">
          <div className="rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
            <p className="text-[13px] font-medium text-kw-warning-text">Pending approval</p>
            <p className="mt-0.5 text-[12px] text-kw-warning-text/90">
              {rock.pendingApprovers.length > 0
                ? `Waiting on: ${rock.pendingApprovers.map((a) => APPROVER_LABEL[a]).join(', ')}`
                : 'Waiting on approval.'}
            </p>
          </div>

          {canApprove ? (
            rejecting ? (
              <div className="space-y-2">
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                  rows={2}
                  placeholder="Reason (optional)"
                  aria-label="Rejection reason"
                  className={TEXTAREA_CLS}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={rejectMutation.isPending}
                    onClick={() => {
                      setRejecting(false);
                      setReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    isLoading={rejectMutation.isPending}
                    onClick={() => {
                      rejectMutation.mutate();
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  isLoading={approveMutation.isPending}
                  disabled={busy}
                  onClick={() => {
                    approveMutation.mutate();
                  }}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setRejecting(true);
                  }}
                >
                  Reject
                </Button>
              </div>
            )
          ) : null}
        </div>
      ) : null}

      {rock.approvalState === 'active' ? (
        <div className="flex items-center gap-2 rounded-kw-md border border-kw-border bg-kw-bg/50 p-3">
          <CheckCircle2 className="size-4 shrink-0 text-kw-tag-text" />
          <p className="text-[12px] text-kw-muted">
            Active{rock.activatedAt ? ` since ${formatDate(rock.activatedAt)}` : ''}.
          </p>
        </div>
      ) : null}
    </div>
  );
}
